import { useMemo, useState } from 'react';
import { Avatar, Typography, Divider, Tooltip, Dropdown, Popover, Modal } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faReply,
    faReplyAll,
    faShare,
    faPaperclip,
    faEllipsisH,
    faFaceSmile,
    faBorderAll,
    faTag,
    faFlag,
    faCircleInfo,
    faEnvelope,
    faPrint,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import TrackingPanel from '../Outlook/TrackingPanel';

const { Text } = Typography;

const AVATAR_COLORS = [
    '#1890ff', '#52c41a', '#fa8c16', '#f5222d',
    '#722ed1', '#13c2c2', '#eb2f96', '#2f54eb',
];

const COMMON_EMOJIS = ['👍', '❤️', '😊', '🎉', '👏', '🙏', '✅', '🔥', '💯', '😂', '😮', '😢'];

const getInitials = (name) =>
    name?.split(' ')?.map((n) => n[0])?.join('').slice(0, 2).toUpperCase() ?? '?';

const getAvatarColor = (name) => {
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) {
        hash = (name.charCodeAt(i) + ((hash << 5) - hash)) | 0;
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

function stripTrackingPixels(html) {
    if (!html) return '';
    return html.replace(/<img[^>]*\/api\/track\/open\/[^>]*\/?>/gi, '');
}

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) return timeStr;
    const dateFormatted = d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    return `${dateFormatted} ${timeStr}`;
};

const stripHtml = (html) => {
    if (!html) return '';
    const cleaned = html
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<!--[\s\S]*?-->/g, '');
    const div = document.createElement('div');
    div.innerHTML = cleaned;
    return div.textContent || div.innerText || '';
};

// ── Small icon button matching Outlook header action style ────────────────────
const IconBtn = ({ icon, title, onClick, active, activeColor }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <Tooltip title={title}>
            <div
                onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 30, height: 30, borderRadius: 4, cursor: 'pointer',
                    backgroundColor: hovered ? '#f3f2f1' : 'transparent',
                    transition: 'background-color 0.12s',
                }}
            >
                <FontAwesomeIcon
                    icon={icon}
                    style={{ fontSize: 14, color: active ? (activeColor || '#0078d4') : hovered ? '#0078d4' : '#605e5c' }}
                />
            </div>
        </Tooltip>
    );
};

// ── Bottom Reply / Forward button (last card only) ────────────────────────────
const BottomBtn = ({ icon, label, onClick }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '6px 20px',
                border: `1px solid ${hovered ? '#9ca3af' : '#d1d5db'}`,
                borderRadius: 4,
                backgroundColor: hovered ? '#f3f4f6' : '#fff',
                cursor: 'pointer', fontSize: 13,
                color: '#374151', fontWeight: 500,
                fontFamily: 'inherit',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                transition: 'background 0.12s, border-color 0.12s',
            }}
        >
            <FontAwesomeIcon icon={icon} style={{ fontSize: 12, color: '#605e5c' }} />
            {label}
        </button>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────
const ReplyCard = ({
    reply,
    onReplyClick,
    onReplyAllClick,
    onForwardClick,
    minimized: initialMinimized,
    index,
    isLast,
    repliedOnDate,
    userEmail,
    // Functional action callbacks — passed from parent
    onDelete,
    onMarkUnread,
    onFlag,
    isFlagged: isFlaggedProp,
}) => {
    const [showQuoted, setShowQuoted] = useState(false);
    const [isMinimized, setIsMinimized] = useState(initialMinimized);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [reactions, setReactions] = useState([]);
    const [viewModalOpen, setViewModalOpen] = useState(false);

    const { mainContent, quotedContent } = useMemo(() => {
        const src = reply.bodyPreview || '';
        const srcLower = src.toLowerCase();

        // Patterns (in priority order) that indicate the start of quoted/previous email content.
        // We take the earliest match so the main body stays as short as possible.
        const separators = [
            '<hr',                          // Outlook <hr> divider
            '<blockquote',                  // Standard HTML blockquote (Outlook, Apple Mail, etc.)
            '<div class="gmail_quote"',     // Gmail inline quote
            '<div id="appendedtext"',       // Some older Outlook versions
        ];

        let splitIdx = -1;
        for (const sep of separators) {
            const idx = srcLower.indexOf(sep);
            if (idx !== -1 && (splitIdx === -1 || idx < splitIdx)) {
                splitIdx = idx;
            }
        }

        if (splitIdx === -1) return { mainContent: src, quotedContent: '' };
        return { mainContent: src.slice(0, splitIdx), quotedContent: src.slice(splitIdx) };
    }, [reply.bodyPreview]);

    const senderName = reply.fromName || reply.from || '';
    const senderEmail = reply.fromName ? reply.from : '';
    const avatarColor = getAvatarColor(senderName);
    const isOwnSent = reply.provider === 'ses-sent' || reply.provider === 'ses-sent-reply' ||
        (userEmail && reply.from && (reply.from === userEmail || reply.fromName === userEmail));

    // isFlagged: prefer prop (Redux state from parent), fall back to email data
    const isFlagged = isFlaggedProp !== undefined
        ? isFlaggedProp
        : reply.flag?.flagStatus === 'flagged';

    const downloadAttachment = (attachment) => {
        if (!attachment.contentBytes) return;
        const byteCharacters = atob(attachment.contentBytes);
        const byteArray = new Uint8Array(
            new Array(byteCharacters.length).fill().map((_, i) => byteCharacters.charCodeAt(i))
        );
        const blob = new Blob([byteArray], { type: attachment.contentType });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = attachment.name;
        link.click();
    };

    // Print just this email's content in a new window
    const handlePrint = () => {
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        if (!printWindow) return;
        printWindow.document.write(`
            <!DOCTYPE html><html><head>
            <title>${reply.subject || 'Email'}</title>
            <style>
                body { font-family: Arial, sans-serif; padding: 24px; font-size: 14px; line-height: 1.6; color: #222; }
                .meta { color: #555; margin-bottom: 16px; font-size: 13px; }
                .meta p { margin: 2px 0; }
                hr { border: none; border-top: 1px solid #ddd; margin: 16px 0; }
            </style>
            </head><body>
            <h2 style="font-size:18px;margin-bottom:12px;">${reply.subject || '(No subject)'}</h2>
            <div class="meta">
                <p><strong>From:</strong> ${senderName}${senderEmail ? ` &lt;${senderEmail}&gt;` : ''}</p>
                <p><strong>To:</strong> ${reply.toNames || reply.to || ''}</p>
                ${reply.ccRecipients ? `<p><strong>Cc:</strong> ${reply.ccRecipients}</p>` : ''}
                <p><strong>Date:</strong> ${formatDate(reply.receivedDateTime)}</p>
            </div>
            <hr/>
            ${stripTrackingPixels(reply.bodyPreview || '')}
            </body></html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    const toggleReaction = (emoji) => {
        setReactions(prev =>
            prev.includes(emoji) ? prev.filter(e => e !== emoji) : [...prev, emoji]
        );
        setShowEmojiPicker(false);
    };

    const emojiPickerContent = (
        <div
            onClick={e => e.stopPropagation()}
            style={{ display: 'flex', flexWrap: 'wrap', gap: 6, width: 220, padding: 4 }}
        >
            {COMMON_EMOJIS.map(emoji => (
                <span
                    key={emoji}
                    onClick={() => toggleReaction(emoji)}
                    style={{
                        fontSize: 20, cursor: 'pointer', padding: '4px 6px',
                        borderRadius: 6, lineHeight: 1,
                        backgroundColor: reactions.includes(emoji) ? '#e6f4ff' : 'transparent',
                        border: reactions.includes(emoji) ? '1px solid #91caff' : '1px solid transparent',
                        transition: 'background 0.12s',
                    }}
                >
                    {emoji}
                </span>
            ))}
        </div>
    );

    const moreMenuItems = {
        items: [
            {
                key: 'markUnread',
                label: 'Mark as unread',
                icon: <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 13, marginRight: 6 }} />,
                disabled: !onMarkUnread,
            },
            {
                key: 'flag',
                label: isFlagged ? 'Remove flag' : 'Flag this message',
                icon: <FontAwesomeIcon icon={faFlag} style={{ fontSize: 13, marginRight: 6, color: '#c50f1f' }} />,
                disabled: !onFlag,
            },
            { type: 'divider' },
            {
                key: 'print',
                label: 'Print',
                icon: <FontAwesomeIcon icon={faPrint} style={{ fontSize: 13, marginRight: 6 }} />,
            },
            { type: 'divider' },
            {
                key: 'delete',
                label: <span style={{ color: '#c50f1f' }}>Delete</span>,
                icon: <FontAwesomeIcon icon={faTrash} style={{ fontSize: 13, color: '#c50f1f', marginRight: 6 }} />,
                disabled: !onDelete,
            },
        ],
        onClick: ({ key }) => {
            if (key === 'markUnread' && onMarkUnread) onMarkUnread(reply);
            if (key === 'flag' && onFlag) onFlag(reply);
            if (key === 'print') handlePrint();
            if (key === 'delete' && onDelete) onDelete(reply);
        },
    };

    // ── Minimised (collapsed) view ────────────────────────────────────────────
    if (isMinimized) {
        return (
            <div
                onClick={() => setIsMinimized(false)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 16px',
                    backgroundColor: '#fff',
                    border: '1px solid #e1dfdd',
                    borderRadius: 4,
                    marginBottom: 4,
                    cursor: 'pointer',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#faf9f8'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fff'; }}
            >
                <Avatar
                    size={32}
                    style={{
                        backgroundColor: isOwnSent ? '#6366f1' : avatarColor,
                        flexShrink: 0, fontSize: 12, fontWeight: 600,
                    }}
                >
                    {getInitials(senderName)}
                </Avatar>
                <Text strong style={{ minWidth: 120, flexShrink: 0, fontSize: 13, color: '#201f1e' }}>
                    {senderName}
                </Text>
                <Text type="secondary" style={{
                    flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', fontSize: 13,
                }}>
                    {stripHtml(mainContent).slice(0, 140)}
                </Text>
                <Text type="secondary" style={{ flexShrink: 0, fontSize: 12 }}>
                    {formatDate(reply.receivedDateTime)}
                </Text>
            </div>
        );
    }

    // ── Expanded view ─────────────────────────────────────────────────────────
    return (
        <>
            <div style={{
                marginBottom: 6,
                border: '1px solid #e1dfdd',
                borderRadius: 4,
                backgroundColor: '#fff',
                overflow: 'hidden',
            }}>
                {/* ── Card Header ─────────────────────────────────────────── */}
                <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '12px 16px',
                }}>
                    {/* Avatar */}
                    <Avatar
                        size={40}
                        style={{
                            backgroundColor: isOwnSent ? '#6366f1' : avatarColor,
                            flexShrink: 0, fontSize: 14, fontWeight: 700, marginTop: 2,
                        }}
                    >
                        {getInitials(senderName)}
                    </Avatar>

                    {/* Sender name + To row */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                            <Text strong style={{ fontSize: 14, color: '#201f1e' }}>{senderName}</Text>
                            {senderEmail && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    &lt;{senderEmail}&gt;
                                </Text>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                To:&nbsp;{reply.toNames || reply.to}
                            </Text>
                            {reply.ccRecipients && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    &nbsp;&nbsp;Cc:&nbsp;{reply.ccRecipients}
                                </Text>
                            )}
                            {reply.bccRecipients && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    &nbsp;&nbsp;Bcc:&nbsp;{reply.bccRecipients}
                                </Text>
                            )}
                        </div>
                    </div>

                    {/* Action icons + timestamp */}
                    <div style={{
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'flex-end', gap: 4, flexShrink: 0,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {/* Emoji reaction picker */}
                            <Popover
                                trigger="click"
                                open={showEmojiPicker}
                                onOpenChange={setShowEmojiPicker}
                                content={emojiPickerContent}
                                placement="bottomLeft"
                            >
                                <div onClick={e => e.stopPropagation()}>
                                    <IconBtn icon={faFaceSmile} title="React with emoji" active={reactions.length > 0} />
                                </div>
                            </Popover>

                            <IconBtn icon={faReply} title="Reply" onClick={() => onReplyClick(reply)} />
                            <IconBtn icon={faReplyAll} title="Reply All" onClick={() => onReplyAllClick && onReplyAllClick(reply)} />
                            <IconBtn icon={faShare} title="Forward" onClick={() => onForwardClick(reply)} />

                            {/* Divider */}
                            <div style={{ width: 1, height: 16, backgroundColor: '#c8c6c4', margin: '0 4px' }} />

                            {/* Flag toggle */}
                            <IconBtn
                                icon={faTag}
                                title={isFlagged ? 'Remove flag' : 'Flag this message'}
                                onClick={() => onFlag && onFlag(reply)}
                                active={isFlagged}
                                activeColor="#c50f1f"
                            />

                            {/* View full email */}
                            <IconBtn
                                icon={faBorderAll}
                                title="View full email"
                                onClick={() => setViewModalOpen(true)}
                            />

                            {/* Three-dot more options */}
                            <Dropdown menu={moreMenuItems} trigger={['click']} placement="bottomRight">
                                <div onClick={e => e.stopPropagation()}>
                                    <IconBtn icon={faEllipsisH} title="More options" />
                                </div>
                            </Dropdown>

                            {/* Collapse — not on first card */}
                            {index !== 0 && (
                                <Tooltip title="Collapse">
                                    <div
                                        onClick={(e) => { e.stopPropagation(); setIsMinimized(true); }}
                                        style={{
                                            width: 30, height: 30, display: 'flex',
                                            alignItems: 'center', justifyContent: 'center',
                                            cursor: 'pointer', color: '#605e5c', fontSize: 10,
                                            borderRadius: 4, transition: 'background 0.12s',
                                        }}
                                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f3f2f1'; }}
                                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                    >
                                        ▲
                                    </div>
                                </Tooltip>
                            )}
                        </div>

                        <Text type="secondary" style={{ fontSize: 12, paddingRight: 4 }}>
                            {formatDate(reply.receivedDateTime)}
                        </Text>
                    </div>
                </div>

                {/* ── Emoji reactions row ──────────────────────────────────── */}
                {reactions.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, padding: '0 16px 8px 66px', flexWrap: 'wrap' }}>
                        {reactions.map(emoji => (
                            <Tooltip key={emoji} title="Click to remove">
                                <span
                                    onClick={() => toggleReaction(emoji)}
                                    style={{
                                        fontSize: 16, cursor: 'pointer',
                                        padding: '2px 8px', borderRadius: 12,
                                        border: '1px solid #e0e0e0',
                                        backgroundColor: '#f5f5f5',
                                        userSelect: 'none',
                                    }}
                                >
                                    {emoji}
                                </span>
                            </Tooltip>
                        ))}
                    </div>
                )}

                {/* ── Flagged row ──────────────────────────────────────────── */}
                {isFlagged && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '0 16px 8px 66px',
                    }}>
                        <FontAwesomeIcon icon={faFlag} style={{ color: '#c50f1f', fontSize: 13 }} />
                        <Text style={{ color: '#c50f1f', fontSize: 13, fontWeight: 500 }}>Flagged</Text>
                    </div>
                )}

                <Divider style={{ margin: 0 }} />

                {/* ── SES tracking timeline (own outbound emails only) ────── */}
                {isOwnSent && (reply.trackId || reply.id || reply.messageId) && (
                    <TrackingPanel email={reply} />
                )}

                {/* ── "You replied on" info row ────────────────────────────── */}
                {repliedOnDate && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '7px 16px',
                        backgroundColor: '#f9f9f9',
                        borderBottom: '1px solid #f0f0f0',
                    }}>
                        <FontAwesomeIcon icon={faCircleInfo} style={{ color: '#605e5c', fontSize: 13, flexShrink: 0 }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            You replied on {formatDate(repliedOnDate)}
                        </Text>
                    </div>
                )}

                {/* ── Body ────────────────────────────────────────────────── */}
                <div style={{ padding: '14px 16px', maxHeight: 500, overflowY: 'auto' }}>
                    <div
                        style={{
                            maxWidth: '100%', wordBreak: 'break-word',
                            overflowWrap: 'break-word', fontSize: 13, lineHeight: 1.7,
                        }}
                        dangerouslySetInnerHTML={{
                            __html: stripTrackingPixels(mainContent.replace(/\r\n/g, '<br />')),
                        }}
                    />

                    {/* Quoted text toggle */}
                    {quotedContent && (
                        <div style={{ marginTop: 10 }}>
                            <Tooltip title={showQuoted ? 'Hide quoted text' : 'Show quoted text'}>
                                <span
                                    onClick={() => setShowQuoted((v) => !v)}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        padding: '2px 12px', borderRadius: 4,
                                        border: '1px solid #d9d9d9',
                                        backgroundColor: showQuoted ? '#f0f0f0' : '#fafafa',
                                        cursor: 'pointer', color: '#888',
                                        fontSize: 13, userSelect: 'none',
                                    }}
                                >
                                    <FontAwesomeIcon icon={faEllipsisH} style={{ fontSize: 11 }} />
                                </span>
                            </Tooltip>
                        </div>
                    )}

                    {showQuoted && quotedContent && (
                        <div
                            style={{
                                marginTop: 8, paddingLeft: 12,
                                borderLeft: '3px solid #d9d9d9',
                                color: '#888', fontSize: 13,
                            }}
                            dangerouslySetInnerHTML={{
                                __html: stripTrackingPixels(quotedContent.replace(/\r\n/g, '<br />')),
                            }}
                        />
                    )}
                </div>

                {/* ── Attachments ──────────────────────────────────────────── */}
                {reply.attachments?.length > 0 && (
                    <div style={{ padding: '0 16px 12px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {reply.attachments.map((att) => {
                                const isImage = att.contentType?.startsWith('image/');
                                return (
                                    <Tooltip key={att.id} title={`${att.name} (${(att.size / 1024).toFixed(1)} KB)`}>
                                        {isImage && att.contentBytes ? (
                                            <img
                                                src={`data:${att.contentType};base64,${att.contentBytes}`}
                                                alt={att.name}
                                                onClick={() => downloadAttachment(att)}
                                                style={{
                                                    maxWidth: 160, maxHeight: 120, borderRadius: 4,
                                                    cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                                                }}
                                            />
                                        ) : (
                                            <div
                                                onClick={() => downloadAttachment(att)}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 6,
                                                    padding: '5px 10px', border: '1px solid #e8e8e8',
                                                    borderRadius: 4, cursor: 'pointer',
                                                    backgroundColor: '#fafafa', fontSize: 12,
                                                    color: '#0078d4', maxWidth: 200,
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faPaperclip} style={{ flexShrink: 0, color: '#888' }} />
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{att.name}</span>
                                            </div>
                                        )}
                                    </Tooltip>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ── Reply / Forward bottom buttons — last message only ───── */}
                {isLast && (
                    <div style={{
                        display: 'flex', gap: 8,
                        padding: '12px 16px 16px',
                        borderTop: '1px solid #f0f0f0',
                    }}>
                        <BottomBtn icon={faReply} label="Reply" onClick={() => onReplyClick(reply)} />
                        {(reply.ccRecipients || reply.toNames?.includes(',')) && (
                            <BottomBtn icon={faReplyAll} label="Reply All" onClick={() => onReplyAllClick && onReplyAllClick(reply)} />
                        )}
                        <BottomBtn icon={faShare} label="Forward" onClick={() => onForwardClick(reply)} />
                    </div>
                )}
            </div>

            {/* ── View full email modal ────────────────────────────────────── */}
            <Modal
                open={viewModalOpen}
                onCancel={() => setViewModalOpen(false)}
                footer={null}
                width="80%"
                title={
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: '#201f1e' }}>
                            {reply.subject || '(No subject)'}
                        </div>
                        <div style={{ fontSize: 12, color: '#605e5c', fontWeight: 400, marginTop: 4 }}>
                            {senderName}{senderEmail ? ` <${senderEmail}>` : ''} · {formatDate(reply.receivedDateTime)}
                        </div>
                    </div>
                }
            >
                <div
                    style={{ fontFamily: 'inherit', fontSize: 14, lineHeight: 1.7, maxHeight: '70vh', overflowY: 'auto', paddingTop: 8 }}
                    dangerouslySetInnerHTML={{ __html: stripTrackingPixels(reply.bodyPreview || '') }}
                />
            </Modal>
        </>
    );
};

export default ReplyCard;
